import type { AppErrorKind } from '@/lib/telemetry/faro';
import { getGraphQLErrorCode } from '@/lib/graphql-error';
import {
  CONFIG_ASSEMBLY_ERRORS,
  PROVIDER_TRANSIENT_ASSEMBLY_ERRORS,
  STALE_SIGNATURE_ERRORS,
  USER_FILE_ASSEMBLY_ERRORS,
} from '@/lib/transloadit-codes';

/**
 * Upload error classification for telemetry — the browser half of the upload pipeline.
 *
 * Mirrors `payment-errors.ts`: explicit sets, and anything in none of them is `unclassified`
 * and STILL reported. The assembly stage classifies by the Transloadit response-code
 * reference (`transloadit-codes.ts`, kept parallel to the upload-processor webhook lane):
 * our config → `systemic`; provider-transient → `unclassified` (rate is the signal); the
 * user's own file → not reported at all (`isUserFileUploadError`), the same way a declined
 * card is not reported on the payment lane — the toast covers the user and the webhook lane
 * holds the denominator. Unknown codes stay `unclassified`; widen from prod data.
 *
 * Stages (one `AppFlow` each — see faro.ts):
 *   options   `getFileUploadOptions` — if THIS fails the upload dialog never opens.
 *   assembly  Uppy → Transloadit / Transforge (the bytes).
 *   save      `saveAsset` after the assembly is accepted.
 *   thumbnail the thumbnail-replace variant (its own options + assembly + save).
 */
export type UploadStage = 'options' | 'assembly' | 'save' | 'thumbnail';

export interface UploadErrorClass {
  kind: AppErrorKind;
  code: string;
}

const STAGE_FALLBACK: Record<UploadStage, string> = {
  options: 'options_query_failed',
  assembly: 'assembly_failed',
  save: 'save_failed',
  thumbnail: 'thumbnail_failed',
};

/** A GraphQL rejection the UI should have blocked (permission / limit / auth). */
const GUARD_CODES = new Set([
  'FORBIDDEN',
  'UNAUTHENTICATED',
  'PERMISSION_DENIED',
  'CREATION_LIMIT',
]);

/** Backend gate messages we know by name — kept as their own code, never as free text. */
const MESSAGE_CODES: Array<[RegExp, string]> = [
  [/S3 and DigitalOcean Spaces are disabled/i, 'storage_disabled'],
  [/Upload Files via API is not enabled/i, 'api_upload_not_enabled'],
  [/Account not found/i, 'account_missing'],
];

/** Bounded, provider-shaped code (e.g. `INPUT_FILE_EMPTY`). Anything else is dropped. */
const CODE_SHAPE = /^[A-Z0-9_]{1,64}$/;

/** The user cancelled — not a failure, never reported. */
export function isUploadCancelled(err: unknown): boolean {
  try {
    return (err as { name?: unknown } | undefined)?.name === 'AbortError';
  } catch {
    return false;
  }
}

function messageOf(err: unknown): string {
  try {
    const m = (err as { message?: unknown } | undefined)?.message;
    return typeof m === 'string' ? m : '';
  } catch {
    return '';
  }
}

/** Apollo Client 4 transport failure → `{kind: systemic, code}` or undefined. */
function networkFailure(err: unknown): UploadErrorClass | undefined {
  const ne = (err as { networkError?: { statusCode?: unknown } | null } | undefined)?.networkError;
  if (!ne) return undefined;
  const status =
    typeof ne === 'object' && typeof ne.statusCode === 'number' ? ne.statusCode : undefined;
  return { kind: 'systemic', code: status ? `http_${status}` : 'network' };
}

function classifyGraphQL(err: unknown, stage: UploadStage): UploadErrorClass | undefined {
  const net = networkFailure(err);
  if (net) return net;
  const isGql = !!err && typeof err === 'object' && 'graphQLErrors' in err;
  if (!isGql) return undefined;
  const code = getGraphQLErrorCode(err);
  if (code && GUARD_CODES.has(code)) return { kind: 'guard_regression', code };
  for (const [re, known] of MESSAGE_CODES) {
    if (re.test(messageOf(err))) return { kind: 'unclassified', code: known };
  }
  // A backend code we do not recognise: keep it (bounded) so prod tells us what it is.
  if (code && CODE_SHAPE.test(code)) return { kind: 'unclassified', code };
  return { kind: 'unclassified', code: STAGE_FALLBACK[stage] };
}

/**
 * The provider's own error code, from any shape Uppy's Transloadit plugin produces:
 * creation-time — `new Error(assembly.error)` with `error.assembly = status`; socket
 * `assembly_error` — `Object.assign(new Error(status.msg), status)` so `error.error` is the
 * code; our own direct `fetch` path — `new Error(assembly.error)`. Bounded, or undefined.
 */
function assemblyCodeOf(err: unknown): string | undefined {
  try {
    const e = err as
      | { assembly?: { error?: unknown }; error?: unknown; message?: unknown }
      | undefined;
    for (const candidate of [e?.assembly?.error, e?.error, e?.message]) {
      if (typeof candidate === 'string' && CODE_SHAPE.test(candidate)) return candidate;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * True when the assembly failed because of the USER's file (too large, unsupported,
 * malicious, an unreachable Drive/Dropbox source, …). Never reported from the browser.
 */
export function isUserFileUploadError(err: unknown): boolean {
  const code = assemblyCodeOf(err);
  return !!code && USER_FILE_ASSEMBLY_ERRORS.has(code);
}

function classifyAssembly(err: unknown): UploadErrorClass {
  const assemblyErr = assemblyCodeOf(err);
  if (assemblyErr) {
    // An expired 1h signature is the user's timing, not our key — see STALE_SIGNATURE_ERRORS.
    if (STALE_SIGNATURE_ERRORS.has(assemblyErr)) return { kind: 'unclassified', code: assemblyErr };
    // Deliberately `systemic`, not `config_regression`: logs-stack-v2's FirebaseAuthConfigRegression
    // rule selects on kind with no evt filter and would page the AUTH on-call (same constraint
    // as payment-errors.ts). The code is preserved, so the distinction is not lost.
    if (CONFIG_ASSEMBLY_ERRORS.has(assemblyErr)) return { kind: 'systemic', code: assemblyErr };
    // Provider-transient and anything unlisted: rate-thresholded, never page on first.
    if (PROVIDER_TRANSIENT_ASSEMBLY_ERRORS.has(assemblyErr))
      return { kind: 'unclassified', code: assemblyErr };
    return { kind: 'unclassified', code: assemblyErr };
  }
  // Transport (Uppy / tus): the bytes never reached the provider, so there is no provider
  // code. The webhook lane sees this same event as REQUEST_ABORTED (expected); the browser's
  // job is WHOSE network — one host is that host, many hosts together is the endpoint.
  const transport = err as { isNetworkError?: unknown } | undefined;
  if (transport && typeof transport === 'object' && transport.isNetworkError === true) {
    return { kind: 'client_network', code: 'network' };
  }
  const msg = messageOf(err);
  if (msg.startsWith('tus: ')) {
    const status = /response code: (\d{3})/.exec(msg)?.[1];
    if (status)
      return { kind: status.startsWith('5') ? 'systemic' : 'unclassified', code: `http_${status}` };
    return { kind: 'client_network', code: 'tus_failed' };
  }
  if (err instanceof TypeError && /fetch/i.test(messageOf(err))) {
    return { kind: 'client_network', code: 'fetch_failed' };
  }
  const status = /status (\d{3})/.exec(messageOf(err))?.[1];
  if (status)
    return { kind: status.startsWith('5') ? 'systemic' : 'unclassified', code: `http_${status}` };
  return { kind: 'unclassified', code: STAGE_FALLBACK.assembly };
}

/**
 * Classify a caught upload error for `reportAppError`. Never throws; a hostile value
 * classifies as the stage fallback.
 */
export function classifyUploadError(stage: UploadStage, err: unknown): UploadErrorClass {
  try {
    const gql = classifyGraphQL(err, stage);
    if (gql) return gql;
    if (stage === 'assembly') return classifyAssembly(err);
    if (
      stage === 'options' &&
      /Failed to get upload options|configuration unavailable/i.test(messageOf(err))
    ) {
      return { kind: 'systemic', code: 'options_empty' };
    }
    if (err instanceof TypeError && /fetch/i.test(messageOf(err))) {
      return { kind: 'client_network', code: 'fetch_failed' };
    }
    return { kind: 'unclassified', code: STAGE_FALLBACK[stage] };
  } catch {
    return { kind: 'unclassified', code: STAGE_FALLBACK[stage] };
  }
}
